import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { Play, Pause, SkipBack, SkipForward, VolumeX, Volume2 } from 'lucide-react';

interface AudioWaveformProps {
  url: string;
  onTimeUpdate?: (currentTime: number) => void;
  onDurationChange?: (duration: number) => void;
  onWaveformInit?: (wavesurfer: WaveSurfer) => void;
  pitch?: number;
  tempo?: number;
  volume?: number;
  disableControls?: boolean;
}

export const AudioWaveform: React.FC<AudioWaveformProps> = ({ 
  url, 
  onTimeUpdate, 
  onDurationChange,
  onWaveformInit,
  pitch = 0,
  tempo = 1,
  volume = 1,
  disableControls = false
}) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const wavesurfer = useRef<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(volume * 100);
  const maxRetries = 3;
  const retryDelay = 1000; // 1 second
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!url) {
      setLoadError('Audio URL is missing');
      return;
    }

    let retryCount = 0;
    let retryTimeout: NodeJS.Timeout;
    let isMounted = true;
    
    const loadAudio = async () => {
      if (!isMounted) return;
      
      setIsLoading(true);
      
      try {
        if (!waveformRef.current) {
          return;
        }

        if (wavesurfer.current) {
          wavesurfer.current.pause();
          wavesurfer.current.destroy();
          wavesurfer.current = null;
        }

        const wsInstance = WaveSurfer.create({
          container: waveformRef.current,
          waveColor: '#8469e6',
          progressColor: '#6366f1',
          cursorColor: '#ffffff',
          barWidth: 2,
          barGap: 1,
          height: 50,
          barRadius: 3,
          normalize: true,
          fetchParams: { mode: 'cors' },
        });

        const mediaEl = wsInstance.getMediaElement();
        if (mediaEl) {
          mediaEl.crossOrigin = 'anonymous';
        }

        wavesurfer.current = wsInstance;
        if (onWaveformInit) {
          onWaveformInit(wsInstance);
        }

        wsInstance.on('error', async (error) => {
          if (!isMounted) return;
          
          console.error('WaveSurfer error:', error);
          
          if (retryCount < maxRetries) {
            retryCount++;
            retryTimeout = setTimeout(loadAudio, retryDelay);
          } else {
            setLoadError(`Failed to load audio after ${maxRetries} attempts: ${error.message || 'Unknown error'}`);
            setIsLoading(false);
          }
        });

        wsInstance.on('ready', () => {
          if (!isMounted) return;
          
          setLoadError(null);
          setIsLoading(false);
          if (onDurationChange) {
            onDurationChange(wsInstance.getDuration());
          }
          
          // Apply initial settings
          // Ensure playback rate is exactly 1.0 by default unless explicitly changed
          wsInstance.setPlaybackRate(tempo === 1 ? 1.0 : tempo);
          wsInstance.setVolume(volume);
        });

        wsInstance.on('audioprocess', (time) => {
          if (isMounted && onTimeUpdate) {
            onTimeUpdate(time);
          }
        });
        
        wsInstance.on('finish', () => {
          if (isMounted) {
            setIsPlaying(false);
          }
        });

        if (!url) {
          setLoadError('Invalid audio URL');
          setIsLoading(false);
          return;
        }

        try {
          const response = await fetch(url, { 
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache'
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          if (!isMounted) return;
          
          const blob = await response.blob();
          const objectURL = URL.createObjectURL(blob);
          
          await wsInstance.load(objectURL);
          URL.revokeObjectURL(objectURL);
          
        } catch (fetchError) {
          console.error('Fetch error:', fetchError);
          setLoadError(`Failed to load audio: ${fetchError.message}`);
          setIsLoading(false);
        }

      } catch (error) {
        if (!isMounted) return;
        
        if (error instanceof Error) {
          console.error('Error loading audio:', error);
          setLoadError(error.message);
        }
        setIsLoading(false);
      }
    };

    loadAudio();

    return () => {
      isMounted = false;
      clearTimeout(retryTimeout);
      
      if (wavesurfer.current) {
        wavesurfer.current.pause();
        try {
          wavesurfer.current.destroy();
        } catch (error) {
          console.error('Error during wavesurfer cleanup:', error);
        }
        wavesurfer.current = null;
      }
    };
  }, [url, onTimeUpdate]);

  // Update settings when they change
  useEffect(() => {
    if (!wavesurfer.current) return;

    // Ensure playback rate is exactly 1.0 by default unless explicitly changed
    wavesurfer.current.setPlaybackRate(tempo === 1 ? 1.0 : tempo);
    wavesurfer.current.setVolume(isMuted ? 0 : volume);
  }, [tempo, volume, isMuted]);

  const togglePlayPause = () => {
    if (wavesurfer.current) {
      wavesurfer.current.playPause();
      setIsPlaying(!isPlaying);
    }
  };
  
  const toggleMute = () => {
    if (wavesurfer.current) {
      setIsMuted(!isMuted);
      wavesurfer.current.setVolume(isMuted ? volume : 0);
    }
  };
  
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseInt(e.target.value) / 100;
    setVolumeLevel(parseInt(e.target.value));
    if (wavesurfer.current) {
      wavesurfer.current.setVolume(newVolume);
    }
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  return (
    <div className="bg-kod-purple-800/50 rounded-lg p-4">
      {loadError ? (
        <div className="text-red-500 text-center mb-4">
          Error: {loadError}
        </div>
      ) : null}
      
      {isLoading && !loadError && (
        <div className="text-center mb-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-kod-accent"></div>
          <p className="mt-2 text-white/70">Loading audio...</p>
        </div>
      )}
      
      <div ref={waveformRef} className="mb-4" />
      
      {!disableControls && (
        <div className="flex flex-col space-y-3" data-testid="waveform-controls">
          <div className="flex justify-center space-x-3">
            <button
              onClick={() => wavesurfer.current?.seekTo(0)}
              className="bg-black/50 hover:bg-black/70 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors"
              disabled={!!loadError || isLoading}
            >
              <SkipBack size={16} />
            </button>
            <button
              onClick={togglePlayPause}
              className="bg-kod-accent hover:bg-kod-accent-dark text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors"
              disabled={!!loadError || isLoading}
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} fill="currentColor" />}
            </button>
            <button
              onClick={() => {
                if (wavesurfer.current?.getDuration()) {
                  wavesurfer.current?.seekTo(1);
                }
              }}
              className="bg-black/50 hover:bg-black/70 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors"
              disabled={!!loadError || isLoading}
            >
              <SkipForward size={16} />
            </button>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={toggleMute}
              className="text-white/60 hover:text-white"
              disabled={!!loadError || isLoading}
            >
              {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            <input
              type="range"
              min="0"
              max="100"
              value={isMuted ? 0 : volumeLevel}
              onChange={handleVolumeChange}
              className="flex-1 h-1.5 bg-kod-purple-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-kod-accent"
              disabled={!!loadError || isLoading}
            />
            <span className="text-xs text-white/60 w-8 text-right">{isMuted ? 0 : Math.round(volumeLevel)}%</span>
          </div>
        </div>
      )}
    </div>
  );
};